<template>
  <div class="br-tab-stock-alert">
    <section>
      <KpiCards class="mb-3" :items="kpiItems" :loading="!kpiReady" />

      <!-- Main report panel -->
      <v-card class="br-panel mb-3" flat>
        <v-card-text class="pb-2 pt-3">
          <!-- Panel header: title + tabs -->
          <div class="d-flex align-center flex-wrap br-panel-header">
            <span class="br-accent-bar" />
            <h1 class="br-panel-title">{{ $t("stock_alert_report") }}</h1>
            <span class="br-panel-count">{{ pagination.total }} {{ $t("items") }}</span>
            <v-spacer />
            <div class="br-tabs">
              <div
                v-for="t in typeList"
                :key="t.value"
                class="br-tab"
                :class="{ 'br-tab--active': type_filter === t.value }"
                @click="type_filter = t.value"
              >
                {{ $t(t.text) }}
              </div>
            </div>
          </div>

          <!-- Filter row -->
          <div class="d-flex align-center flex-wrap br-filter-row mt-3">
            <TextSearch
              v-model="filters.q"
              class="br-search"
              @search="fetchItems(1)"
              @onClear="
                filters.q = '';
                fetchItems(1);
              "
            />

            <TextSelect
              class="br-select"
              dense
              searchable
              :disabled="type_filter === 'ingredient'"
              :placeholder="false"
              label="category"
              itemValue="id"
              text="name"
              :returnObject="false"
              :items="categoryFilterOptions"
              v-model="filters.cat_id"
              hide-details
              @input="fetchItems(1)"
            />

            <TextSelect
              class="br-select"
              dense
              searchable
              :placeholder="false"
              label="supplier"
              itemValue="id"
              text="name"
              :returnObject="false"
              :items="supplierFilterOptions"
              v-model="filters.vendor_id"
              hide-details
              @input="fetchItems(1)"
            />

            <span v-if="isFiltered" class="br-clear-link" @click="resetFilters">
              {{ $t("clear_filters") }}
            </span>

            <v-spacer />

            <div class="br-export-btn" @click="handleExcelExport">
              <v-progress-circular
                v-if="exporting"
                size="13"
                width="2"
                indeterminate
                color="primary"
                class="mr-1"
              />
              <v-icon v-else size="13" class="mr-1">fal fa-file-export</v-icon>
              {{ $t("export") }}
            </div>
          </div>

          <!-- Data table -->
          <div class="br-table-wrap mt-3">
            <div
              v-if="!overlay && items.length === 0"
              class="d-flex flex-column align-center justify-center py-12"
            >
              <v-icon size="64" color="grey lighten-1">fal fa-box-open</v-icon>
              <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-2">
                {{ $t("no_low_stock_items") }}
              </div>
              <div class="mt-1 grey--text text-body-2">
                {{ $t("no_low_stock_items_description") }}
              </div>
            </div>

            <v-data-table
              v-else
              :headers="headers"
              :items="items"
              item-key="id"
              :loading="overlay"
              :loading-text="$t('loading')"
              :no-data-text="$t('no_data')"
              :server-items-length="pagination.total"
              :options.sync="options"
              :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
              class="br-data-table elevation-0"
              fixed-header
            >
              <template v-slot:item.index="{ index }">
                <span class="text-caption grey--text">
                  {{ (pagination.page - 1) * pagination.per_page + index + 1 }}
                </span>
              </template>

              <template v-slot:item.name="{ item }">
                <div class="font-weight-medium text-body-2">
                  {{ displayName(item) }}
                </div>
              </template>

              <template v-slot:item.type="{ item }">
                <span
                  class="br-tag"
                  :class="item.type === 'product' ? 'br-tag--info' : 'br-tag--warning'"
                >
                  {{ $t(item.type) }}
                </span>
              </template>

              <template v-slot:item.category_name="{ item }">
                <span class="text-body-2 grey--text text--darken-2">
                  {{ categoryDisplay(item) }}
                </span>
              </template>

              <template v-slot:item.current_stock="{ item }">
                <span class="br-tag br-tag--error font-weight-bold">
                  {{ item.current_stock | NumberFormat }}
                </span>
              </template>

              <template v-slot:item.unit_name="{ item }">
                <span class="text-body-2">{{ unitLabel(item) }}</span>
              </template>

              <template v-slot:item.last_restocked_at="{ item }">
                <span v-if="item.last_restocked_at" class="text-caption font-numeric">
                  {{ item.last_restocked_at | moment("DD/MM/YYYY") }}
                </span>
                <span v-else class="text-caption grey--text">
                  {{ $t("never_restocked") }}
                </span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>

      <app-loading :overlay="overlay && !exporting" />
    </section>
  </div>
</template>

<script>
import TextSelect from "@/components/global/TextSelect";
import TextSearch from "@/components/global/TextSearch";
import { stockAlertReportHead } from "@/utils/head";
import { toast, error } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";

export default {
  name: "StockAlertReportPage",
  mixins: [myMixin],
  components: {
    TextSelect,
    TextSearch,
  },
  data: () => ({
    overlay: false,
    kpiReady: false,
    exporting: false,
    type_filter: "all",
    categories: [],
    suppliers: [],
    items: [],
    filters: {
      cat_id: "",
      vendor_id: "",
      q: "",
    },
    options: {
      page: 1,
      itemsPerPage: 30,
    },
    pagination: {
      total: 0,
      page: 1,
      per_page: 30,
    },
  }),
  async created() {
    await Promise.all([
      this.fetchCategories(),
      this.fetchSuppliers(),
      this.fetchItems(),
    ]);
    this.kpiReady = true;
  },
  computed: {
    rowsPerPageItems() {
      return this.$store.getters["index/rowsPerPageItems"] || [15, 30, 50];
    },
    kpiItems() {
      return [
        {
          key: "alert",
          title: "stock_alert",
          icon: "fal fa-exclamation-triangle",
          value: this.pagination.total,
          note: this.$t("items"),
        },
        {
          key: "product",
          title: "product",
          icon: "fal fa-box-open",
          value: this.productAlertCount,
          note: this.$t("low_stock"),
        },
        {
          key: "ingredient",
          title: "ingredient",
          icon: "fal fa-mortar-pestle",
          value: this.ingredientAlertCount,
          note: this.$t("low_stock"),
        },
        {
          key: "supplier",
          title: "supplier",
          icon: "fal fa-truck-loading",
          value: this.suppliers.length,
          note: this.$t("active"),
        },
      ];
    },
    typeList() {
      return [
        { value: "all", text: "all" },
        { value: "product", text: "product" },
        { value: "ingredient", text: "ingredient" },
      ];
    },
    isFiltered() {
      return Boolean(
        this.filters.cat_id ||
          this.filters.vendor_id ||
          this.filters.q ||
          this.type_filter !== "all"
      );
    },
    productAlertCount() {
      if (this.type_filter === "product") return this.pagination.total;
      return this.items.filter((i) => i.type === "product").length;
    },
    ingredientAlertCount() {
      if (this.type_filter === "ingredient") return this.pagination.total;
      return this.items.filter((i) => i.type === "ingredient").length;
    },
    categoryFilterOptions() {
      const locale = this.$i18n.locale;
      const key =
        { en: "en_name", th: "th_name", cn: "cn_name", ch: "cn_name" }[
          locale
        ] || "la_name";
      return [
        { id: "", name: this.$t("all") },
        ...this.categories.map((c) => ({
          id: c.id,
          name: c[key] || c.la_name || String(c.id),
        })),
      ];
    },
    supplierFilterOptions() {
      return [{ id: "", name: this.$t("all") }, ...this.suppliers];
    },
    headers() {
      return stockAlertReportHead(this);
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchItems();
      },
    },
    type_filter(next) {
      if (next === "ingredient" && this.filters.cat_id) {
        this.filters.cat_id = "";
      }
      this.fetchItems(1);
    },
  },
  methods: {
    resetFilters() {
      this.filters.cat_id = "";
      this.filters.vendor_id = "";
      this.filters.q = "";
      this.type_filter = "all";
      this.fetchItems(1);
    },
    async handleExcelExport() {
      this.exporting = true;
      try {
        const params = new URLSearchParams();
        if (this.type_filter !== "all") params.append("type", this.type_filter);
        if (this.filters.q) params.append("q", this.filters.q);
        if (this.type_filter !== "ingredient" && this.filters.cat_id) {
          params.append("cat_id", this.filters.cat_id);
        }
        if (this.filters.vendor_id) {
          params.append("vendor_id", this.filters.vendor_id);
        }
        params.append("locale", this.$i18n.locale);

        const data = await this.$http.get(
          `download_stock_alert_report?${params.toString()}`,
          { responseType: "blob" }
        );

        const blobUrl = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        const fileName = `stock_alert_report_${Date.now()}.xlsx`;

        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(blobUrl);

        toast(this, "excel_export_success");
      } catch (err) {
        console.log("Error exporting Excel:", err);
        error(this, "excel_export_failed");
      } finally {
        this.exporting = false;
      }
    },
    async fetchItems(page) {
      if (page) this.options.page = page;
      try {
        this.overlay = true;
        const params = new URLSearchParams();
        if (this.type_filter !== "all") params.append("type", this.type_filter);
        if (this.filters.q) params.append("q", this.filters.q);
        if (this.type_filter !== "ingredient" && this.filters.cat_id) {
          params.append("cat_id", this.filters.cat_id);
        }
        if (this.filters.vendor_id) {
          params.append("vendor_id", this.filters.vendor_id);
        }
        params.append("page", this.options.page);
        params.append("per_page", this.options.itemsPerPage);

        const { data } = await this.$http.get(
          `stock_alert_report?${params.toString()}`
        );
        if (data.status) {
          this.items = (Array.isArray(data.data.data) ? data.data.data : []).map(
            this.mapRow
          );
          this.pagination = {
            total: data.data.total || 0,
            page: data.data.current_page || 1,
            per_page: data.data.per_page || this.options.itemsPerPage,
          };
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.overlay = false;
      }
    },
    mapRow(row) {
      return {
        ...row,
        source_id: row.id,
        id: `${row.type}:${row.id}`,
      };
    },
    async fetchCategories() {
      try {
        const { data } = await this.$http.get("categories");
        if (data && data.status && Array.isArray(data.data)) {
          this.categories = data.data;
        }
      } catch (err) {
        console.log(err);
      }
    },
    async fetchSuppliers() {
      try {
        const { data } = await this.$http.get("suppliers");
        this.suppliers = Array.isArray(data.data) ? data.data : [];
      } catch (err) {
        console.log(err);
      }
    },
    displayName(item) {
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      if (locale === "th" && item.name_th) return item.name_th;
      if ((locale === "cn" || locale === "ch") && item.name_cn) {
        return item.name_cn;
      }
      return item.name || item.name_en || item.name_th || item.name_cn || "-";
    },
    unitLabel(item) {
      if (this.$i18n.locale === "en" && item.unit_name_en) {
        return item.unit_name_en;
      }
      return item.unit_name || "-";
    },
    categoryDisplay(item) {
      if (item.type === "ingredient") {
        return item.vendor_name || "-";
      }
      if (this.$i18n.locale === "en" && item.category_name_en) {
        return item.category_name_en;
      }
      return item.category_name || "-";
    },
  },
};
</script>

<style scoped>
.br-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-accent-soft: rgba(91, 57, 201, 0.12);
  --br-accent-softer: rgba(91, 57, 201, 0.07);
  --br-accent-border: rgba(91, 57, 201, 0.35);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.br-page--dark {
  --br-text: #ffffff;
  --br-border: rgba(255, 255, 255, 0.08);
  background: #1b1d30;
}

.br-page--dark .br-panel,
.br-page--dark .br-panel.v-card.v-sheet {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.br-page section {
  width: 100%;
}

/* Main panel */
.br-panel,
.br-panel.v-card.v-sheet {
  background: #ffffff !important;
  border: 1px solid var(--br-border) !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}
.br-panel-header {
  gap: 10px;
}
.br-accent-bar {
  width: 3px;
  height: 15px;
  background: var(--br-accent);
  border-radius: 2px;
}
.br-panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: var(--br-text);
}
.br-panel-count {
  font-size: 11.5px;
  color: var(--br-muted);
}
.br-tabs {
  display: flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid var(--br-border);
}
.br-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: var(--br-muted);
  transition: all 0.15s ease;
}
.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}
.br-page--dark .br-tab--active {
  color: #c4b5fd !important;
  background: rgba(154, 140, 240, 0.18) !important;
  box-shadow: none;
}

/* Filter row */
.br-filter-row {
  gap: 10px;
}
.br-search {
  flex: 1 1 220px;
  min-width: 210px;
  max-width: 300px;
}
.br-select {
  max-width: 180px;
}
.br-clear-link {
  font-size: 12px;
  color: var(--br-accent);
  cursor: pointer;
  margin-left: 4px;
}
.br-clear-link:hover {
  text-decoration: underline;
}
.br-export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: var(--br-radius-md);
  border: 1px solid var(--br-border);
  color: var(--br-muted);
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  user-select: none;
}
.br-export-btn:hover {
  color: var(--br-text);
  border-color: #cbd5e1;
}

/* Tags */
.br-tag {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid transparent;
}
.br-tag--info {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}
.br-tag--warning {
  color: #c2410c;
  background: #fff7ed;
  border-color: #fed7aa;
}
.br-tag--error {
  color: #b3261e;
  background: #fdecea;
  border-color: #f6c6c2;
}

/* Table styling */
.br-table-wrap ::v-deep .v-data-table {
  background: transparent !important;
}
.br-table-wrap ::v-deep .v-data-table-header th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
  color: var(--br-muted) !important;
  border-bottom: 1px solid var(--br-border) !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}
.br-table-wrap ::v-deep .v-data-table tbody tr {
  border-bottom: 1px solid #f1f2f7 !important;
}
.br-table-wrap ::v-deep .v-data-table tbody tr:hover {
  background: var(--br-accent-softer) !important;
}

.font-numeric {
  font-variant-numeric: tabular-nums;
}

.br-page--dark .br-table-wrap ::v-deep .v-data-table-header th {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}
.br-page--dark .br-table-wrap ::v-deep .v-data-table tbody tr {
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}
</style>
